/**
 * Block 10 (2026-05-27): FOP annual-limit dashboard widget.
 *
 * Multi-org aggregation для tenants з ≥1 FOP-org:
 * - Loads all FOP-orgs (filter is_fop)
 * - Bulk endpoint → sorted checks + summary counts
 * - Default filter: non-OK rows only (show OK rows toggle)
 * - Click row → drill-down /fop/reports?org=X (state-prefilled)
 *
 * Empty state коли tenant без FOP-orgs — без bulk API call.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import {
  fopReports,
  type AnnualLimitCheck,
  type WarningLevel,
} from '@/api/fop_reports';
import {
  WARNING_COLOR,
  WARNING_LABEL_UA,
} from '@/shared/warning_colors';
import LimitCheckBadge from '@/shared/components/LimitCheckBadge';
import DashboardWidget from '../DashboardWidget';

/** ч.843: ЮРОСОБА-єдинник (3 група) — теж у віджеті лімітів (ФОП ловиться is_fop). */
const SINGLE_TAX_SYSTEMS = new Set(['SIMPLIFIED_3']);

export default function FopLimitsWidget() {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const currentYear = new Date().getFullYear();

  const orgListQ = useQuery({
    queryKey: ['organizations', 'fop-widget'],
    queryFn: () => orgs.list({ limit: 200 }),
  });
  // ч.844: віджет слідує за фільтром організації у шапці (порожній — усі).
  // ч.843: єдинники — ФОП 1-3 (is_fop + спрощена) І ЮРОСОБИ 3 групи (SIMPLIFIED_3);
  // бек однаково відсіє зайве, але не шлемо в bulk свідомо не-єдинників.
  const [orgFilter] = useOrgFilter();
  const fopOrgIds = useMemo(
    () => (orgListQ.data?.items ?? [])
      .filter((o) => !orgFilter || o.id === orgFilter)
      .filter((o) => o.is_fop || SINGLE_TAX_SYSTEMS.has(o.tax_system))
      .map((o) => o.id),
    [orgListQ.data, orgFilter],
  );

  const bulkQ = useQuery({
    queryKey: ['fop-limits-bulk', fopOrgIds, currentYear],
    queryFn: () => fopReports.annualLimitCheckBulk(fopOrgIds, currentYear),
    enabled: fopOrgIds.length > 0,
    staleTime: 60_000, // 1 хв — limits не змінюються часто
  });

  const checks: AnnualLimitCheck[] = bulkQ.data?.checks ?? [];
  // Діаграма — коли йдеться про ОДНУ організацію (обрану в шапці або єдину).
  const single = checks.length === 1 ? checks[0] : undefined;
  const summary = bulkQ.data?.summary;
  const visibleChecks = showAll
    ? checks
    : checks.filter((c) => c.warning_level !== 'OK');

  const loading = orgListQ.isLoading || (fopOrgIds.length > 0 && bulkQ.isLoading);
  const isEmpty = !loading && fopOrgIds.length === 0;

  return (
    <DashboardWidget
      title="Ліміти єдинників"
      loading={loading}
      isEmpty={isEmpty}
      emptyMessage="Немає організацій-єдинників у вашому тенанті"
      actions={
        checks.length > 0 && !single ? (
          <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
            Показати всі ({checks.length})
          </label>
        ) : undefined
      }
    >
      {/* ч.847 (рішення власника): обрана організація — кругова діаграма
          використання ліміту, під нею сума використання. */}
      {single && <LimitDonut data={single} />}

      {!single && summary && (
        <div
          data-testid="fop-limits-summary"
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: '0.5rem',
            fontSize: '0.82rem',
          }}
        >
          {(['CRITICAL', 'WARNING', 'INFO', 'OK'] as WarningLevel[]).map((lvl) => {
            const key = `${lvl.toLowerCase()}_count` as
              | 'critical_count' | 'warning_count' | 'info_count' | 'ok_count';
            const count = summary[key];
            if (count === 0) return null;
            // Якщо showAll=false і це OK — позначаємо «приховано».
            const hidden = !showAll && lvl === 'OK';
            return (
              <span
                key={lvl}
                style={{
                  padding: '2px 8px',
                  borderRadius: 12,
                  background: WARNING_COLOR[lvl] + '22',
                  color: WARNING_COLOR[lvl],
                  fontWeight: 500,
                }}
              >
                {count} {WARNING_LABEL_UA[lvl]}
                {hidden ? ' (приховано)' : ''}
              </span>
            );
          })}
        </div>
      )}

      <div
        data-testid="fop-limits-list"
        style={{ display: single ? 'none' : 'flex', flexDirection: 'column', gap: 6 }}
      >
        {visibleChecks.map((check) => (
          <LimitCheckBadge
            key={check.organization_id}
            data={check}
            mode="compact"
            onClick={() =>
              navigate(
                `/fop/reports?org=${check.organization_id}&tab=limit`,
              )
            }
          />
        ))}
        {visibleChecks.length === 0 && checks.length > 0 && !showAll && (
          <p style={{ color: '#10b981', fontStyle: 'italic' }}>
            ✓ Усі {checks.length} ФОП у нормі — без попереджень
          </p>
        )}
      </div>
    </DashboardWidget>
  );
}


/** ч.847: кільцева діаграма використання річного ліміту + сума під нею. */
function LimitDonut({ data }: { data: AnnualLimitCheck }) {
  const navigate = useNavigate();
  const color = WARNING_COLOR[data.warning_level];
  const pctRaw = Number(data.usage_percent);
  const pct = Number.isFinite(pctRaw) ? pctRaw : 0;
  const shown = Math.max(0, Math.min(pct, 100));
  // ч.848 (рішення власника): діаграма вдвічі менша (70×70 замість 140×140).
  const R = 27;
  const C = 2 * Math.PI * R;
  const fmt = (v: string | number) =>
    Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div
      data-testid="fop-limit-donut"
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/fop/reports?org=${data.organization_id}&tab=limit`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/fop/reports?org=${data.organization_id}&tab=limit`);
        }
      }}
      style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
      title={data.warning_message}
    >
      <svg width={70} height={70} viewBox="0 0 70 70" style={{ flex: '0 0 auto' }} aria-hidden>
        <circle cx={35} cy={35} r={R} fill="none" stroke="#e2e8f0" strokeWidth={7} />
        <circle
          cx={35} cy={35} r={R} fill="none" stroke={color} strokeWidth={7}
          strokeLinecap="round"
          strokeDasharray={`${(C * shown) / 100} ${C}`}
          transform="rotate(-90 35 35)"
        />
        <text x={35} y={39} textAnchor="middle" fontSize={13} fontWeight={600} fill={color}>
          {pct.toFixed(1)}%
        </text>
      </svg>
      {/* ч.848: сума використання — ПРАВОРУЧ від діаграми, у два рядки. */}
      <div style={{ fontSize: '0.85rem', lineHeight: 1.35 }}>
        <div><b>{fmt(data.revenue_ytd)}</b></div>
        <div style={{ color: 'var(--muted)' }}>з {fmt(data.annual_limit)} грн</div>
      </div>
    </div>
  );
}
